/**
 * plugins/auth.js
 *
 * Keycloak authentication plugin for Vue 3 + Pinia.
 * Handles Keycloak initialization and provides auth state management.
 */

import Keycloak from "keycloak-js";
import { defineStore } from "pinia";
import { keycloakConfig } from "@/config/keycloak.config";

let keycloakInstance = null;

export async function initKeycloak() {
  if (!keycloakConfig.useKeycloak) {
    console.warn("Keycloak is disabled via configuration.");
    return false;
  }

  keycloakInstance = new Keycloak(keycloakConfig);

  try {
    const authenticated = await keycloakInstance.init({
      onLoad: keycloakConfig.onLoad,
      checkLoginIframe: false, // Set to false to avoid CORS/Iframe issues in dev
      pkceMethod: "S256",
    });

    if (authenticated) {
      // Periodic token refresh
      setInterval(async () => {
        try {
          await refreshAccessToken();
        } catch (error) {
          console.error("Token refresh failed in background:", error);
        }
      }, 60000);
    }

    return authenticated;
  } catch (error) {
    console.error("Failed to initialize Keycloak:", error);
    throw error;
  }
}

export function getKeycloakInstance() {
  if (!keycloakInstance)
    throw new Error("Keycloak not initialized. Call initKeycloak() first.");

  return keycloakInstance;
}

export function getAccessToken() {
  const kc = getKeycloakInstance();
  return kc.token;
}

export async function refreshAccessToken() {
  const kc = getKeycloakInstance();
  try {
    const refreshed = await kc.updateToken(70);
    if (refreshed) {
      const authStore = useAuthStore();
      authStore.token = kc.token;
    }
    return kc.token;
  } catch (error) {
    console.error("Failed to refresh token:", error);
    throw error;
  }
}

export async function logout() {
  const kc = getKeycloakInstance();
  try {
    await kc.logout();
  } catch (error) {
    console.error("Failed to logout:", error);
  }
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    authenticated: false,
    user: null,
    token: null,
  }),

  getters: {
    isAuthenticated: (state) => state.authenticated,
    currentUser: (state) => state.user,
    accessToken: (state) => state.token,
    userProfile: () => {
      const kc = keycloakInstance;
      if (kc && kc.idTokenParsed) {
        return {
          username: kc.idTokenParsed.preferred_username,
          email: kc.idTokenParsed.email,
          firstName: kc.idTokenParsed.given_name,
          lastName: kc.idTokenParsed.family_name,
          name: kc.idTokenParsed.name,
        };
      }
      return null;
    },
  },

  actions: {
    async initializeAuth(authenticated) {
      this.authenticated = authenticated;
      if (authenticated) {
        const kc = getKeycloakInstance();
        this.token = kc.token;
        this.user = kc.idTokenParsed;
      }
    },

    async refreshToken() {
      try {
        const newToken = await refreshAccessToken();
        this.token = newToken;
      } catch (error) {
        console.error("Token refresh failed in store:", error);
        this.authenticated = false;
        this.token = null;
      }
    },

    async logout() {
      this.authenticated = false;
      this.user = null;
      this.token = null;
      await logout();
    },
  },
});
